import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useSwipeable } from 'react-swipeable/es';

import './styles.css';
import { imagePreloader } from './lib/image_preloader';
import { ImageSliderBullets } from './image_slider_bullets';

type Props = {
  images: string[];
  width?: number | string; // default - 100%
  height?: number | string; // default - 100%
  startIndex?: number; // default - 0
  slideDuration?: number; // default - 300 ms
  autoPlay?: boolean; // default - true
  autoPlayDelay?: number; // default - 3000 ms
};

export const ImageSlider = ({
  images: sourceImages,
  width = '100%',
  height = '100%',
  slideDuration = 300,
  autoPlay = true,
  autoPlayDelay = 3000,
  startIndex = 0,
}: Props) => {
  if (!sourceImages.length) {
    return null;
  }

  const images = useMemo(() => {
    if (sourceImages.length <= 1) return sourceImages;
    return [sourceImages[sourceImages.length - 1], ...sourceImages, sourceImages[0]];
  }, [sourceImages]);

  const isSlidingRef = useRef(false);

  const [isAutoplaying, setIsAutoplaying] = useState(autoPlay);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(startIndex + 1);

  const ref = useRef<HTMLDivElement>(null);

  const pauseAutoPlay = () => setIsAutoplaying(false);

  const resumeAutoPlay = () => setIsAutoplaying(true);

  const showSlide = (index: number, animation = true) => {
    if (!ref.current) return Promise.resolve();

    Object.assign(ref.current.style, {
      transition: animation ? `${slideDuration}ms` : 'none',
      transform: `translateX(-${index * 100}%)`,
    });

    if (animation)
      return new Promise((resolve) => {
        ref.current.addEventListener('transitionend', resolve, { once: true });
      });

    return Promise.resolve();
  };

  const goTo = (index: number, animation = true) => {
    if (isSlidingRef.current || images.length === 1 || index === currentSlideIndex) return;

    isSlidingRef.current = true;

    showSlide(index, animation).then(() => {
      setCurrentSlideIndex(index);
      isSlidingRef.current = false;

      if (index === images.length - 1) {
        goTo(1, false);
      }
      if (index === 0) {
        goTo(images.length - 2, false);
      }
    });
  };

  const swipeHanlders = useSwipeable({
    onSwipedRight: () => goTo(currentSlideIndex - 1),
    onSwipedLeft: () => goTo(currentSlideIndex + 1),
  });

  useEffect(() => {
    goTo(startIndex + 1, false);
  }, [images]);

  useEffect(() => {
    let timeout: number | null = null;

    if (isAutoplaying) {
      timeout = window.setTimeout(() => {
        goTo(currentSlideIndex + 1);
      }, autoPlayDelay + slideDuration);
    }

    return () => timeout && window.clearTimeout(timeout);
  }, [isAutoplaying, currentSlideIndex]);

  useEffect(() => {
    const nextImage = images[currentSlideIndex + 1];
    const prevImage = images[currentSlideIndex - 1];
    if (nextImage) imagePreloader.load(nextImage);
    if (prevImage) imagePreloader.load(prevImage);
  }, [currentSlideIndex]);

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div
      className="image-slider"
      style={{ width, height }}
      onMouseEnter={pauseAutoPlay}
      onMouseLeave={resumeAutoPlay}
      onTouchStart={pauseAutoPlay}
      onTouchEnd={resumeAutoPlay}
      {...swipeHanlders}
    >
      <div className="image-slider__slides" ref={ref} onClick={() => goTo(currentSlideIndex + 1)}>
        {images.map((image) => (
          <div style={{ backgroundImage: `url(${image})` }} className="image-slider__slide" />
        ))}
      </div>
      {images.length > 1 && (
        <ImageSliderBullets
          length={images.length - 2}
          currentIdx={currentSlideIndex - 1}
          onClickBullets={(idx) => goTo(idx + 1)}
        />
      )}
    </div>
  );
};
